import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X } from "lucide-react";

import {
  useAppReportsStore,
  Report as FrontendReport,
} from "../../../stores/appReportsStore";
import { fetchReports, deleteReport, calculateReportTotals } from "../../../services/report/reportService";
import { useAuth } from "../../../services/authentication/authContext";
import { formatDate, currencyFormatter } from "../../../services/helperService";
import { useTranslation } from "react-i18next";
import { useRestaurantStore } from "../../../stores/appRestaurantStore";
import MobilePageHeader from "../../shared/mobile/MobilePageHeader";

export const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: authUser } = useAuth();

  const reportsById = useAppReportsStore(
    (s) => s.reportsById as Record<string, FrontendReport>
  );
  const reports = Object.values(reportsById);
  const setReports = useAppReportsStore((s) => s.setReportsById);
  const removeReport = useAppReportsStore((s) => s.removeReport);
  const getProductsIdMap = useAppReportsStore((s) => s.getProductsIdMap);
  const getSelectedOptionIdMap = useAppReportsStore((s) => s.getSelectedOptionIdMap);

  const restaurants = useRestaurantStore(s => s.restaurants);
  const isLoading = restaurants.length === 0; 

  useEffect(() => {
    if (!authUser) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const data = await fetchReports();
        const map: Record<string, FrontendReport> = Object.fromEntries(
          data.map((r) => [r.id, r])
        );
        setReports(map);

      } catch (err: any) {
        console.error(err);
      }
    })();
  }, [authUser, navigate, setReports]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("reports.confirmDelete") || "Delete is irreversible. Continue?"))
      return;
    try {
      await deleteReport(id);
      removeReport(id);
    } catch (err: any) {
      console.error(err);
      alert(t("reports.deleteError") || "Could not delete report");
    }
  };

  if (isLoading) return <p>Loading...</p>;

  // Group reports by restaurant
  const reportsByRestaurant = restaurants
    .map((rest) => ({
      ...rest,
      reports: reports.filter((r) => r.restaurantId === rest.id),
    }))
    .filter((grp) => grp.reports.length > 0);

  // Overall aggregate spending & savings
  const aggregateSpending = reports.reduce((sum, r) => {
    const tot = calculateReportTotals(
      getProductsIdMap(r.id) || {},
      getSelectedOptionIdMap(r.id) || {}
    );
    return sum + tot.currentTotal;
  }, 0);
  const aggregateSavings = reports.reduce((sum, r) => {
    const tot = calculateReportTotals(
      getProductsIdMap(r.id) || {},
      getSelectedOptionIdMap(r.id) || {}
    );
    return sum + tot.savings;
  }, 0);

  return (
    <div className="min-h-full p-6">
      <MobilePageHeader title={t("reports.title")} />
      <div className="max-w-6xl mx-auto mb-10 text-center">
        <h1 className="hidden lg:block text-3xl font-bold text-text dark:text-text-dark mb-6">
          {t("reports.title")}
        </h1>
        <div className="flex flex-col items-center gap-4">
          <div>
            <p className="text-sm text-muted dark:text-muted-dark">
              {t("reports.totalSpending") || "Total Spending"}
            </p>
            <p className="text-2xl font-bold text-text dark:text-text-dark">
              {currencyFormatter.format(aggregateSpending)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted dark:text-muted-dark">
              {t("reports.totalSavings") || "Total Savings"}
            </p>
            <p className="text-4xl font-bold text-success dark:text-success-dark">
              {currencyFormatter.format(aggregateSavings)}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-4">
        {reportsByRestaurant.map(({ id: restId, name, reports: restReports }) => {
          // Spending & savings per restaurant
          const restaurantSpending = restReports.reduce((sum, r) => {
            const tot = calculateReportTotals(
              getProductsIdMap(r.id) || {},
              getSelectedOptionIdMap(r.id) || {}
            );
            return sum + tot.currentTotal;
          }, 0);
          const restaurantSavings = restReports.reduce((sum, r) => {
            const tot = calculateReportTotals(
              getProductsIdMap(r.id) || {},
              getSelectedOptionIdMap(r.id) || {}
            );
            return sum + tot.savings;
          }, 0);

          return (
            <details key={restId} className="bg-card dark:bg-card-dark rounded-2xl p-4">
              <summary className="flex max-lg:flex-wrap items-center max-lg:gap-y-2 cursor-pointer">
                <span className="text-xl font-bold text-text dark:text-text-dark">
                  {name}
                </span>
                <span className="ml-4 text-lg font-bold text-text dark:text-text-dark">
                  <span className="text-sm font-medium text-muted dark:text-muted-dark">{t("reports.spending")}:</span> {currencyFormatter.format(restaurantSpending)}
                </span>
                <span className={`ml-auto text-2xl font-bold ${restaurantSavings >= 0 ? 'text-success' : 'text-destructive'}`}>
                <span className="text-sm font-medium text-muted dark:text-muted-dark">{t("reports.savings")}:</span> {currencyFormatter.format(Math.abs(restaurantSavings))}
                </span>
              </summary>


              <ul className="mt-4 space-y-2">
                {restReports.map((r) => {
                  const createdFormatted = formatDate(r.createdAt);
                  const startFormatted = formatDate(r.startDate);
                  const endFormatted = formatDate(r.endDate);
                  const totals = calculateReportTotals(
                    getProductsIdMap(r.id) || {},
                    getSelectedOptionIdMap(r.id) || {}
                  );

                  return (
                    <li
                      key={r.id}
                      className="bg-card-light dark:bg-card-dark-light rounded-2xl p-4 flex max-lg:flex-wrap items-center justify-between max-lg:gap-y-2 hover:bg-card-light transition"
                    >
                      <div className="flex items-center">
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900 transition"
                          aria-label={t("reports.delete") || "Delete report"}
                        >
                          <X className="w-5 h-5 text-destructive dark:text-destructive-dark" />
                        </button>
                        <Link to={`/reports-legacy/${r.id}`} className="ml-3">
                          <div className="text-text dark:text-text-dark">
                            <span className="text-sm text-muted dark:text-muted-dark">
                              {createdFormatted}
                            </span>{" "}• {r.name}{" "}
                            <span className="text-sm text-muted dark:text-muted-dark">
                              • {startFormatted} {t("reports.until")} {endFormatted}
                            </span>
                          </div>
                        </Link>
                      </div>

                      <div className="flex items-center space-x-6">
                        <span className="text-lg font-bold text-text dark:text-text-dark">
                          {currencyFormatter.format(totals.currentTotal)}
                        </span>
                        <span className={`text-sm font-medium ${totals.savingsPercentage > 0 ? 'text-success' : 'text-destructive'}`}>
                          {totals.savingsPercentage < 0 ? '+' : '-'}
                          {Math.abs(totals.savingsPercentage).toFixed(2)}%
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </details>
          );
        })}
      </div>
    </div>
  );
};
